import { Button, Card, CardContent, Grid, Stack, TextField } from '@mui/material';
import { addAnomalyPayload, AnomalyAPI, Nullable, RegionAPI } from '@shared';
import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { auditApi } from 'App';
import {
  Empty,
  Heading,
  SelectAnomaly,
  useAnomaliesFormik,
  StartEndTime,
  AnomalyCard,
} from './shared';
import { SuspectedAnomaliesPropsType } from '../AnomaliesSidebar.types';

export function SuspectedAnomalies(props: SuspectedAnomaliesPropsType) {
  const {
    onSeekChange,
    anomalyTypes,
    suspectedRegions,
    refetch,
    auditUuid,
    disabled = false,
    showSuspectPercent = false,
  } = props;

  const queryClient = useQueryClient();

  const [isShowAdding, setIsShowAdding] = useState(false);
  const [currentRegion, setCurrentRegion] = useState({ start: 0, end: 0, uuid: '' });
  const { mutate: addAnomaly } = useMutation({
    mutationFn: (payload: addAnomalyPayload) => auditApi.addAnomaly(auditUuid, payload),
    onSuccess: (data) => {
      queryClient.setQueryData(['anomalies', auditUuid], (oldData: Nullable<AnomalyAPI[]>) => {
        if (oldData) {
          return [data, ...oldData];
        } else {
          refetch();
          return [];
        }
      });
      queryClient.setQueryData(
        ['processing regions', auditUuid],
        (oldData: Nullable<RegionAPI[]>) =>
          oldData?.filter((region) => region.uuid !== data.region.uuid)
      );
      setIsShowAdding(false);
    },
  });

  const { mutate: rejectAnomaly } = useMutation({
    mutationFn: (regionUuid: string) => auditApi.rejectRegion(auditUuid, regionUuid),
    onSuccess: async (data) => {
      queryClient.setQueryData(
        ['rejected regions', auditUuid],
        (oldData: Nullable<RegionAPI[]>) => {
          if (oldData) {
            return [data, ...oldData];
          } else {
            refetch();
            return [];
          }
        }
      );
      queryClient.setQueryData(
        ['processing regions', auditUuid],
        (oldData: Nullable<RegionAPI[]>) => oldData?.filter((region) => region?.uuid !== data?.uuid)
      );
      setIsShowAdding(false);
    },
  });

  const { values, handleChange, handleSubmit } = useAnomaliesFormik(addAnomaly, currentRegion);

  const handleOpen = (start: number, end: number, uuid: string) => {
    if (disabled) {
      return;
    }
    setIsShowAdding(true);
    setCurrentRegion({ start, end, uuid });
    onSeekChange(start, false);
  };

  if (isShowAdding) {
    return (
      <Card variant='outlined'>
        <CardContent
          sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}
          component='form'
          onSubmit={handleSubmit}
        >
          <SelectAnomaly anomalyTypes={anomalyTypes} id={values.id} handleChange={handleChange} />
          <StartEndTime
            start={currentRegion.start}
            end={currentRegion.end}
            onSeekChange={onSeekChange}
            isReplayButton={true}
          />
          <TextField
            fullWidth
            label='Comments'
            multiline
            rows={4}
            name='comment'
            value={values.comment}
            onChange={handleChange}
          />
          <Grid container spacing={1}>
            <Grid item xs={6}>
              <Button
                fullWidth
                disabled={!values.comment}
                variant='contained'
                color='error'
                onClick={() => rejectAnomaly(currentRegion.uuid)}
              >
                Reject
              </Button>
            </Grid>
            <Grid item xs={6}>
              <Button type='submit' disabled={!values.comment} variant='contained' fullWidth>
                Confirm
              </Button>
            </Grid>
          </Grid>
        </CardContent>
      </Card>
    );
  }

  return (
    <>
      <Heading title={'suspected anomalies'} />
      {suspectedRegions?.length === 0 ? (
        <Empty whichAnomaly={'Suspected'} />
      ) : (
        <Stack gap={2}>
          {suspectedRegions?.map(({ suspectType, start, end, uuid, suspectPercent }) => (
            <AnomalyCard
              key={uuid}
              title={suspectType}
              uuid={uuid}
              start={start}
              end={end}
              onSeekChange={onSeekChange}
              handleOpen={handleOpen}
              showSuspectPercent={showSuspectPercent}
              suspectPercent={suspectPercent}
              disabled={disabled}
            />
          ))}
        </Stack>
      )}
    </>
  );
}
